import { useMemo, useState, useEffect, useRef } from "react";
import { useQuery } from "@/hooks/useApi";
import {
  isBarRankingEntity,
  isBarRegion,
  mailingGroupsService,
  type CountryDetail,
  type PickerRegion,
  type UsedRankingEntity,
  type UsedRegion,
} from "@/api/mailing-groups.service";
import type { RankingEntity } from "@/api/researchCms.service";
import { calculateMailingGroupCount, classifyRegionSelection } from "./mailingGroups.utils";

export function regionKey(region: PickerRegion): string {
  return `${region.id ?? "null"}:${region.name ?? ""}`;
}

function byName(a: PickerRegion, b: PickerRegion): number {
  return (a.name ?? "").localeCompare(b.name ?? "");
}

type UseRegionSelectionParams<R extends PickerRegion> = {
  regions: R[];
  usedRegionsById: ReadonlyMap<number | string, unknown>;
  usedRegionsLoading: boolean;
  isBar: (region: Pick<PickerRegion, "type">) => boolean;
  contextKey: string;
  enabled: boolean;
  initialSelectedRegionKeys?: string[];
};

function useRegionSelection<R extends PickerRegion>({
  regions,
  usedRegionsById,
  usedRegionsLoading,
  isBar,
  contextKey,
  enabled,
  initialSelectedRegionKeys,
}: UseRegionSelectionParams<R>) {
  const [selectedRegionKeys, setSelectedRegionKeys] = useState<Set<string>>(new Set());

  const isRegionUsed = (region: PickerRegion): boolean =>
    region.id !== null && usedRegionsById.has(region.id);

  const isRegionSelectable = (region: PickerRegion): boolean =>
    !usedRegionsLoading && !isRegionUsed(region);

  const selectableRegions = useMemo(
    () => regions.filter(isRegionSelectable),
    [regions, usedRegionsById, usedRegionsLoading],
  );

  const selectedRegions = useMemo(
    () => selectableRegions.filter(r => selectedRegionKeys.has(regionKey(r))),
    [selectableRegions, selectedRegionKeys],
  );

  const selectionKind = useMemo(
    () => classifyRegionSelection(selectedRegions, isBar),
    [selectedRegions, isBar],
  );

  const isAllBar = selectionKind === "bar";
  const isMixed = selectionKind === "mixed";

  const groupCount = useMemo(
    () => calculateMailingGroupCount(selectedRegions, isBar),
    [selectedRegions, isBar],
  );

  const toggleRegion = (region: PickerRegion) => {
    if (!isRegionSelectable(region)) return;
    const key = regionKey(region);
    setSelectedRegionKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectAllRegions = () => {
    setSelectedRegionKeys(new Set(selectableRegions.map(regionKey)));
  };

  const selectNonBarRegions = () => {
    setSelectedRegionKeys(new Set(selectableRegions.filter(r => !isBar(r)).map(regionKey)));
  };

  const selectBarRegions = () => {
    setSelectedRegionKeys(new Set(selectableRegions.filter(isBar).map(regionKey)));
  };

  const clearRegions = () => {
    setSelectedRegionKeys(new Set());
  };

  const reset = () => {
    setSelectedRegionKeys(new Set());
  };

  // Reset selection whenever the publication/edition context changes so a stale
  // selection can't leak across publications.
  const contextRef = useRef<string | null>(null);
  useEffect(() => {
    if (contextRef.current !== null && contextRef.current !== contextKey) {
      setSelectedRegionKeys(new Set());
    }
    contextRef.current = contextKey;
  }, [contextKey]);

  // Pre-select the edited group's own regions when the picker opens (edit mode).
  // Tracks the previous `enabled` so it only fires on the false→true transition,
  // and re-applies if the initial keys change while open.
  const prevEnabledRef = useRef(false);
  useEffect(() => {
    if (enabled && !prevEnabledRef.current) {
      setSelectedRegionKeys(new Set(initialSelectedRegionKeys ?? []));
    }
    prevEnabledRef.current = enabled;
  }, [enabled, initialSelectedRegionKeys]);

  return {
    isRegionUsed,
    isRegionSelectable,
    selectableRegions,
    selectedRegions,
    selectedRegionKeys,
    selectionKind,
    isAllBar,
    isMixed,
    groupCount,
    isBar,
    toggleRegion,
    selectAllRegions,
    selectNonBarRegions,
    selectBarRegions,
    clearRegions,
    reset,
  };
}

type UseRegionPickerParams = {
  /** Numeric publication id, or null while unknown. */
  publicationId: number | null;
  /** Edition string, or null/empty while unknown. */
  edition: string | null;
  /**
   * When false, region/used-region queries are skipped (and resolve to null).
   * Lets the caller gate fetches behind an open modal.
   */
  enabled?: boolean;
  /**
   * Region ids to EXCLUDE from the used/disabled set — i.e. the edited group's
   * own regions, which must stay selectable while OTHER groups' regions stay
   * disabled. Caller must pass a stable/memoized array reference.
   */
  excludeUsedRegionIds?: number[];
  /**
   * Region keys to pre-select when the picker (re)opens (edit mode). Caller must
   * pass a stable/memoized array reference.
   */
  initialSelectedRegionKeys?: string[];
};

/**
 * Shared region-picker state used by both the standalone "generate" modal and
 * the contextual "add group" modal. Owns:
 * - countries-details + used-regions queries,
 * - selectable/selected region memos and selection helpers,
 * - selection classification (bar / standard / mixed) and the live group count.
 *
 * It does NOT own the publication/edition selectors — callers supply those —
 * nor the group-name input or submit logic.
 */
export function useRegionPicker({
  publicationId,
  edition,
  enabled = true,
  excludeUsedRegionIds,
  initialSelectedRegionKeys,
}: UseRegionPickerParams) {
  const excludedRegionIds = useMemo(
    () => new Set(excludeUsedRegionIds ?? []),
    [excludeUsedRegionIds],
  );

  const {
    data: countriesData,
    loading: countriesLoading,
    error: countriesError,
  } = useQuery(
    () =>
      enabled && publicationId
        ? mailingGroupsService.getCountriesDetails(publicationId)
        : Promise.resolve(null),
    [enabled, publicationId],
  );

  // Regions already used by another mailing group in this publication+edition
  // (business Rule 4). Rendered disabled to pre-empt the 409 REGION_ALREADY_USED.
  const { data: usedRegionsData, loading: usedRegionsLoading } = useQuery(
    () =>
      enabled && publicationId && edition
        ? mailingGroupsService.getUsedRegions(publicationId, edition)
        : Promise.resolve(null),
    [enabled, publicationId, edition],
  );

  const usedRegionsById = useMemo(() => {
    const map = new Map<number, UsedRegion>();
    for (const used of usedRegionsData?.usedRegions ?? []) {
      // Skip the edited group's own regions so they remain selectable.
      if (excludedRegionIds.has(used.regionId)) continue;
      map.set(used.regionId, used);
    }
    return map;
  }, [usedRegionsData?.usedRegions, excludedRegionIds]);

  const regions = useMemo<CountryDetail[]>(() => {
    const list = countriesData?.countries ?? [];
    return list.filter(c => c.id !== null).sort(byName);
  }, [countriesData?.countries]);

  const selection = useRegionSelection({
    regions,
    usedRegionsById,
    usedRegionsLoading,
    isBar: isBarRegion,
    contextKey: `${publicationId ?? ""}:${edition ?? ""}`,
    enabled,
    initialSelectedRegionKeys,
  });

  return {
    ...selection,
    regions,
    countriesLoading,
    countriesError,
    usedRegionsById,
    usedRegionsLoading,
  };
}

type UseRankingEntityPickerParams = {
  editionId: string | null;
  enabled: boolean;
  excludeUsedRegionIds?: string[];
  initialSelectedRegionKeys?: string[];
};

export function useRankingEntityPicker({
  editionId,
  enabled,
  excludeUsedRegionIds,
  initialSelectedRegionKeys,
}: UseRankingEntityPickerParams) {
  const excludedRegionIds = useMemo(
    () => new Set(excludeUsedRegionIds ?? []),
    [excludeUsedRegionIds],
  );

  const {
    data: rankingEntities,
    loading: countriesLoading,
    error: countriesError,
  } = useQuery(
    () =>
      enabled && editionId
        ? mailingGroupsService.getEditionRankingEntities(editionId)
        : Promise.resolve(null),
    [enabled, editionId],
  );

  const { data: usedRegionsData, loading: usedRegionsLoading } = useQuery(
    () =>
      enabled && editionId
        ? mailingGroupsService.getUsedRankingEntities(editionId)
        : Promise.resolve(null),
    [enabled, editionId],
  );

  const usedRegionsById = useMemo(() => {
    const map = new Map<string, UsedRankingEntity>();
    for (const used of usedRegionsData?.usedRegions ?? []) {
      if (used.rankingEntityId === null || excludedRegionIds.has(used.rankingEntityId)) continue;
      map.set(used.rankingEntityId, used);
    }
    return map;
  }, [usedRegionsData?.usedRegions, excludedRegionIds]);

  const regions = useMemo<RankingEntity[]>(
    () => [...(rankingEntities ?? [])].sort(byName),
    [rankingEntities],
  );

  const selection = useRegionSelection({
    regions,
    usedRegionsById,
    usedRegionsLoading,
    isBar: isBarRankingEntity,
    contextKey: editionId ?? "",
    enabled,
    initialSelectedRegionKeys,
  });

  return {
    ...selection,
    regions,
    countriesLoading,
    countriesError,
    usedRegionsById,
    usedRegionsLoading,
  };
}
